import { css } from '@emotion/css';
import { Dropdown, DropDownWithChevron } from '@repo/ui/molecules';
import { useContext } from 'react';
import { zIndexModal } from '../globalCSS';
import Apple from '../icons/Apple';
import Google from '../icons/Google';
import Microsoft365 from '../icons/Microsoft365';
import Outlook from '../icons/Outlook';
import { Ical } from '../steps/types';
import { AnalyticsContext, MetadataContext } from '../utils/ContextWrapper';
import { sendEvent } from '../utils/sendEvent';
import { useStudyContext } from '../utils/StudyContext';
import { useTranslationsContext } from '../utils/TranslationsContext';

function Button({
    isOpen,
    toggleOpen,
}: {
    isOpen: boolean;
    toggleOpen: (newValue: boolean) => void;
}) {
    const { t } = useTranslationsContext();
    return (
        <DropDownWithChevron
            header={t('confirmationScreen.addToCalendar.title')}
            isOpen={isOpen}
            toggleOpen={toggleOpen}
            buttonVariant="primary"
            ariaLabel={t('confirmationScreen.addToCalendar.title')}
        />
    );
}

const ContentStyle = css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;
    width: 100%;
`;

function Content({
    iconLeft,
    title,
}: {
    iconLeft: React.ReactNode;
    title: string;
}) {
    return (
        <span className={ContentStyle}>
            {iconLeft}
            {title}
        </span>
    );
}

function AddToCalendar({ google, outlook, ics, office365 }: Ical) {
    const { t } = useTranslationsContext();
    const { study } = useStudyContext();
    const analyticsId = useContext(AnalyticsContext).sessionId;
    const demoMode = useContext(MetadataContext).demoMode;
    return (
        <Dropdown
            zIndex={zIndexModal + 1}
            ariaLabel={t('confirmationScreen.addToCalendar.title')}
            Content={Button}
            options={[
                {
                    id: 'outlook',
                    content: <Content iconLeft={<Outlook />} title="Outlook" />,
                    ariaLabel: 'Outlook',
                    onAction: () => {
                        window.open(outlook, '_blank');
                        sendEvent(
                            {
                                analyticsId,
                                studyId: study.id,
                                event: 'embed.directBooking.confirmationScreen.addToCalendar.outlook',
                            },
                            demoMode,
                        );
                    },
                },
                {
                    content: (
                        <Content
                            iconLeft={<Microsoft365 />}
                            title="Outlook office"
                        />
                    ),
                    id: 'microsoft365',
                    ariaLabel: 'Outlook office',
                    onAction: () => {
                        window.open(office365, '_blank');
                        sendEvent(
                            {
                                analyticsId,
                                studyId: study.id,
                                event: 'embed.directBooking.confirmationScreen.addToCalendar.microsoft365',
                            },
                            demoMode,
                        );
                    },
                },
                {
                    id: 'google',
                    content: <Content iconLeft={<Google />} title="Google" />,
                    ariaLabel: 'Google',
                    onAction: () => {
                        window.open(google, '_blank');
                        sendEvent(
                            {
                                analyticsId,
                                studyId: study.id,
                                event: 'embed.directBooking.confirmationScreen.addToCalendar.google',
                            },
                            demoMode,
                        );
                    },
                },
                {
                    id: 'apple',
                    content: <Content iconLeft={<Apple />} title="Apple" />,
                    ariaLabel: 'Apple',
                    onAction: () => {
                        window.open(ics, '_blank');
                        sendEvent(
                            {
                                analyticsId,
                                studyId: study.id,
                                event: 'embed.directBooking.confirmationScreen.addToCalendar.apple',
                            },
                            demoMode,
                        );
                    },
                },
            ]}
        />
    );
}

export default AddToCalendar;
